import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { api, formatApiError, track } from "../lib/api";
import { ConsentCheckboxes } from "./ConsentDocs";
import { TREATMENTS } from "../content/siteContent";
import { locT } from "../content/i18n_extra";

export function SecondOpinionForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", treatment: "", message: "" });
  const [kvkk, setKvkk] = useState(false);
  const [agreement, setAgreement] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!kvkk || !agreement) {
      toast.error(t("consent_required"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/applications", { ...form, kind: "second_opinion", consent_kvkk: kvkk, consent_agreement: agreement });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "application_submitted", kind: "second_opinion", treatment: form.treatment || "" });
      if (window.gtag) window.gtag("event", "generate_lead", { event_category: "second_opinion", event_label: form.treatment || "genel" });
      track("generate_lead", { form: "second_opinion" });
      setDone(true);
      toast.success(t("form_success"));
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 px-4 rounded-2xl bg-white border border-black/10 text-sm text-ink placeholder:text-sub/70 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-shadow";

  return (
    <section id="iletisim" className="bg-white py-28 sm:py-36" data-testid="second-opinion-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <div className="text-xs font-heading font-bold tracking-[0.3em] uppercase text-brand mb-4">{t("opinion_kicker")}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("opinion_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("opinion_sub")}</p>
          <div className="mt-10 space-y-4 text-sm text-ink/70">
            <div className="flex gap-3"><span className="font-heading font-bold text-brand">a.</span> info@2dhealthcare.com</div>
            <div className="flex gap-3"><span className="font-heading font-bold text-brand">b.</span> +90 538 541 11 02 · +90 538 541 10 01 · +90 531 670 24 60</div>
            <div className="flex gap-3"><span className="font-heading font-bold text-brand">c.</span> Küçük Çamlıca Mah. Vefakar Sok. No:1, Üsküdar / İstanbul</div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="bg-ice rounded-[2rem] border border-brand/10 p-8 sm:p-10 space-y-4"
          data-testid="second-opinion-form"
        >
          {done ? (
            <div className="py-14 text-center" data-testid="form-success-state">
              <CheckCircle2 size={52} className="mx-auto text-emerald-500" />
              <p className="mt-5 font-heading text-xl font-bold text-ink">{t("form_success")}</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="form-name" required value={form.name} onChange={set("name")} placeholder={t("form_name")} className={inputCls} />
                <input data-testid="form-email" required type="email" value={form.email} onChange={set("email")} placeholder={t("form_email")} className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="form-phone" value={form.phone} onChange={set("phone")} placeholder={t("form_phone")} className={inputCls} />
                <input data-testid="form-country" value={form.country} onChange={set("country")} placeholder={t("form_country")} className={inputCls} />
              </div>
              <select data-testid="form-treatment" value={form.treatment} onChange={set("treatment")} className={inputCls}>
                <option value="">{t("form_treatment")}</option>
                {TREATMENTS.map((tr) => (
                  <option key={tr.id} value={tr.id}>{locT(tr, lang).title}</option>
                ))}
                <option value="other">—</option>
              </select>
              <textarea data-testid="form-message" value={form.message} onChange={set("message")} placeholder={t("form_message")} rows={4} className="w-full px-4 py-3.5 rounded-2xl bg-white border border-black/10 text-sm text-ink placeholder:text-sub/70 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-shadow resize-none" />
              <ConsentCheckboxes kvkk={kvkk} agreement={agreement} onKvkk={setKvkk} onAgreement={setAgreement} />
              <button
                data-testid="form-submit"
                disabled={busy}
                className="w-full h-13 py-3.5 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? "..." : <><Send size={15} /> {t("form_submit")}</>}
              </button>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}
